import { ApplicationProps, TreeCityWorldGrowthAwardApplication } from '@/models/Application';
import { ApplicationTypes } from '@/models/ApplicationType';
import { ProgramNames } from '@/models/Program';
import { UserType } from '@/models/User';
import { ApplicationStatus } from '@/utils/constants';

const TCUSA_ROUTE_BY_TYPE: Partial<Record<ApplicationTypes, 'statequestions' | 'growthaward'>> = {
  [ApplicationTypes.StateQuestions]: 'statequestions',
  [ApplicationTypes.GrowthAward]: 'growthaward',
};

export const getApplicationHref = (
  applicationId: number | null,
  applicationTypeId: ApplicationTypes | null,
  programId: ProgramNames | null = null
): string | undefined => {
  if (applicationId == null || applicationTypeId == null) {
    return undefined;
  }

  const routeBase =
    programId === ProgramNames.TreeCityUSA
      ? (TCUSA_ROUTE_BY_TYPE[applicationTypeId] ?? 'application')
      : 'application';

  return `/${routeBase}/${applicationId}`;
};

export interface ApplicationNavigationState {
  id?: number | null;
  questions?: boolean;
}

export function getApplicationNavigationState(
  applicationId: number | null,
  applicationTypeId: ApplicationTypes | null,
  programId: ProgramNames | null = null,
  options?: { hasStateQuestions?: boolean }
): ApplicationNavigationState | null {
  if (applicationTypeId == null || programId !== ProgramNames.TreeCityUSA) {
    return null;
  }

  switch (applicationTypeId) {
    case ApplicationTypes.StateQuestions:
      return {
        id: applicationId,
        questions: options?.hasStateQuestions ?? false,
      };
    case ApplicationTypes.GrowthAward:
      return { id: applicationId };
    default:
      return null;
  }
}

type NavigateFn = (to: string, options?: { state?: ApplicationNavigationState }) => void;

export function navigateToApplicationRoute(
  navigate: NavigateFn,
  programId: ProgramNames,
  applicationTypeId: ApplicationTypes,
  applicationId: number | null | undefined,
  options?: { hasStateQuestions?: boolean }
) {
  const href = getApplicationHref(applicationId ?? null, applicationTypeId, programId);

  if (href == null) {
    return;
  }

  const navigationState = getApplicationNavigationState(
    applicationId ?? null,
    applicationTypeId,
    programId,
    options
  );

  if (navigationState != null) {
    navigate(href, { state: navigationState });
    return;
  }

  navigate(href);
}

const REVIEWER_PROGRAMS = new Set<ProgramNames>([
  ProgramNames.TreeCityUSA,
  ProgramNames.TreeCitiesWorld,
  ProgramNames.TreeLineUSA,
  ProgramNames.TreeCampusHigherEducation,
]);

const STATE_REVIEWER_STATUSES = new Set<ApplicationStatus>([
  ApplicationStatus.InProgress,
  ApplicationStatus.PendingRegionalReview,
  ApplicationStatus.PendingStateReview,
]);

export function getApplicationReviewerRoles(
  userType: UserType | null | undefined,
  status: ApplicationStatus | null | undefined,
  programId: ProgramNames | null | undefined
) {
  const isReviewerProgram = programId != null && REVIEWER_PROGRAMS.has(programId);

  return {
    isReviewerProgram,
    isRegionalReviewer:
      userType === UserType.RegionalCoordinator &&
      status === ApplicationStatus.PendingRegionalReview &&
      isReviewerProgram,
    isStateReviewer:
      userType === UserType.StateCoordinator &&
      status != null &&
      STATE_REVIEWER_STATUSES.has(status) &&
      isReviewerProgram,
  };
}

export function isTreeCityWorldGrowthAwardApplication(
  app: ApplicationProps
): app is TreeCityWorldGrowthAwardApplication {
  return (
    app.programId === ProgramNames.TreeCitiesWorld &&
    app.applicationTypeId === ApplicationTypes.GrowthAward &&
    'stars' in app &&
    Array.isArray(app.stars)
  );
}
